// src/hooks/useTokenCalculator.js
import { useState, useEffect, useCallback } from 'react';
import { estimateTokens } from '../utils/tokenUtils';

const useTokenCalculator = (
  messages,
  inputMessage,
  selectedModel,
  attachments = [],
  allProjectFiles = '',
  includeCurrentFiles = false,
  selectedFiles = []
) => {
  const [tokenStats, setTokenStats] = useState({
    totalTokens: 0,
    estimatedCost: 0,
    nextMessageCost: 0,
    conversationCost: 0,
    attachmentTokens: 0,
    inputTokens: 0,
    projectFilesTokens: 0
  });

  const calculateTokenStats = useCallback(() => {
    console.log('Calculating token stats for model:', selectedModel, 'input:', inputMessage?.slice(0, 50));
    
    if (!selectedModel) {
      setTokenStats({
        totalTokens: 0,
        estimatedCost: 0,
        nextMessageCost: 0,
        conversationCost: 0,
        attachmentTokens: 0,
        inputTokens: 0,
        projectFilesTokens: 0
      });
      return;
    }

    // Get model pricing from backend data
    const modelsData = window.modelsCache || { models: [] };
    const modelData = modelsData.models.find(m => m.id === selectedModel);
    const modelPricing = modelData?.pricing?.input || 0.30;
    
    console.log('Model data:', modelData?.displayName, 'Pricing:', modelPricing);

    let totalTokens = 0;

    // Current input - Calculate input tokens first
    const inputTokens = estimateTokens(inputMessage || '', selectedModel);
    console.log('Input tokens:', inputTokens, 'for text:', inputMessage?.slice(0, 30));


    
    // Calculate tokens for the entire conversation that will be sent
    let conversationTokens = 0;
    if (messages && messages.length > 0) {
      // Filter out system messages and get actual conversation content
      const conversationMessages = messages.filter(msg => !msg.isSystemMessage && msg.role !== 'system');
      const conversationString = JSON.stringify(conversationMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      })));
      conversationTokens = estimateTokens(conversationString, selectedModel);
    }
    
    // Also calculate individual message tokens for totalTokens
    messages.forEach((message) => {
      if (message.content) {
        const contentForTokens = message.apiContent || message.content;
        const messageTokens = estimateTokens(contentForTokens, selectedModel);
        totalTokens += messageTokens;
      }
    });

    // Attachments - FIXED: More accurate calculation
    let attachmentTokens = 0;
    if (attachments && attachments.length > 0) {
      attachments.forEach((attachment) => {
        if (attachment.type === 'image') {
          // Standard image token count
          attachmentTokens += 1000;
        } else if (attachment.data && typeof attachment.data === 'string') {
          // For text files, count actual content tokens
          console.log('Calculating tokens for attachment:', attachment.name);
          console.log('Attachment data (first 100 chars):', attachment.data);
          
          const contentTokens = estimateTokens(attachment.data, selectedModel);
          console.log(`Attachment ${attachment.name} tokens: ${contentTokens}`);
          attachmentTokens += contentTokens; // Cap at 10k per file
        } else if (attachment.size) {
          // Fallback estimation based on file size
          const fileSizeKB = attachment.size / 1024;
          const estimatedTokens =fileSizeKB/4 ;
          attachmentTokens += estimatedTokens;
        }
      });
    }

    // Project files - Calculate tokens for actual file content
    let projectFilesTokens = 0;
    console.log('All project files length:', allProjectFiles?.length, 'Include current files:', includeCurrentFiles, 'Selected files:', selectedFiles.length);
    console.log('Selected files:', selectedFiles);
    console.log('allProjectFiles files:', allProjectFiles);
    // console.log('allProjectFiles files:', JSON.parse(allProjectFiles));
      // let parse=JSON.parse(text);

    if (allProjectFiles && allProjectFiles.trim() && (includeCurrentFiles || selectedFiles.length > 0)) {
      // allProjectFiles contains the actual file content (either all files or selected files)
      projectFilesTokens = estimateTokens(allProjectFiles, selectedModel);
      console.log(`Project files tokens: ${allProjectFiles.length} chars = ${projectFilesTokens} tokens`);
    }

    const currentInputTokens = inputTokens + attachmentTokens + projectFilesTokens;
    const totalInputWithConversation = conversationTokens + attachmentTokens + projectFilesTokens;
    const totalContextTokens = totalInputWithConversation;
    
    // Estimate output tokens (AI response)
    const estimatedOutputTokens = Math.min(currentInputTokens * 0.5, 8000); // Estimate 50% of input size, max 8k
    const totalWithOutput = totalContextTokens + estimatedOutputTokens;
    
    // Calculate cost including both input and estimated output
    const inputCost = (totalContextTokens * modelPricing) / 1000000;
    const outputCost = (estimatedOutputTokens * (modelData?.pricing?.output || modelPricing)) / 1000000;
    const nextMessageCost = inputCost + outputCost;
    
    // Debug logging only in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Token calculation:', {
        previousMessages: totalTokens,
        input: inputTokens,
        attachments: attachmentTokens,
        conversation: conversationTokens,
        projectFiles: projectFilesTokens,
        totalInput: totalContextTokens,
        estimatedOutput: estimatedOutputTokens,
        totalWithOutput: totalWithOutput,
        inputCost: inputCost.toFixed(6),
        outputCost: outputCost.toFixed(6),
        totalCost: nextMessageCost.toFixed(6)
      });
    }

    setTokenStats({
      totalTokens: totalWithOutput,
      estimatedCost: nextMessageCost,
      nextMessageCost: nextMessageCost,
      conversationCost: 0,
      attachmentTokens: attachmentTokens,
      inputTokens: inputTokens,
      projectFilesTokens: projectFilesTokens,
      conversationTokens: conversationTokens,
      estimatedOutputTokens: estimatedOutputTokens,
      inputCost: inputCost,
      outputCost: outputCost
    });
  }, [
    inputMessage,
    selectedModel,
    attachments,
    includeCurrentFiles,
    selectedFiles,
    allProjectFiles,
    messages
  ]);

  useEffect(() => {
    calculateTokenStats();
  }, [calculateTokenStats]);

  return tokenStats;
};

export { useTokenCalculator };
